import { useCallback, useEffect, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import api from "../api"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { ReactComponent as EmptyIllustration } from "../assets/images/illustrations/il-empty.svg"
import { EditionStatus } from "../common/constants"
import { Each } from "../common/Each"
import Back from "../components/Back"
import Badge from "../components/Badge"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import EmptyContent from "../components/EmptyContent"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import Skeleton from "../components/Skeleton"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import typo from "../typography.module.css"
import LessonEdit from "./LessonEdit"
import styles from "./Lessons.module.css"

const statusOrder = {
  [EditionStatus.Public]: 1,
  [EditionStatus.Ended]: 2,
  [EditionStatus.Locked]: 3,
}

const mergeCollapsedState = (previousState, nextIds, defaultCollapsed = true) => {
  const current = new Map(previousState.map((item) => [item.id, item.collapsed]))
  return nextIds.map((id) => ({
    id,
    collapsed: current.has(id) ? current.get(id) : defaultCollapsed,
  }))
}

const groupAndSortLessonsByCourse = (lessons) => {
  const grouped = lessons.reduce((acc, lesson) => {
    const course = lesson.edition?.course
    if (!course) return acc

    const key = course.id
    if (!acc[key]) {
      acc[key] = {
        course: {
          id: course.id,
          name: course.name,
          thumbnail: course.thumbnail,
        },
        editionsMap: {},
      }
    }

    const editionId = lesson.edition?.id || `no-edition-${course.id}`
    if (!acc[key].editionsMap[editionId]) {
      acc[key].editionsMap[editionId] = {
        edition: {
          id: editionId,
          name: lesson.edition?.name || "Senza edizione",
          status: lesson.edition?.status || EditionStatus.Ended,
        },
        lessons: [],
      }
    }

    acc[key].editionsMap[editionId].lessons.push(lesson)
    return acc
  }, {})

  for (const g of Object.values(grouped)) {
    const now = Date.now()
    const byStartAsc = (a, b) => {
      const ta = a.starts_at ? new Date(a.starts_at).getTime() : 0
      const tb = b.starts_at ? new Date(b.starts_at).getTime() : 0
      return ta - tb
    }

    const editions = Object.values(g.editionsMap).map((editionGroup) => {
      const future = []
      const past = []

      editionGroup.lessons.forEach((lesson) => {
        const endsTime = lesson.ends_at ? new Date(lesson.ends_at).getTime() : (lesson.starts_at ? new Date(lesson.starts_at).getTime() : 0)
        if (endsTime > now) {
          future.push(lesson)
        } else {
          past.push(lesson)
        }
      })

      future.sort(byStartAsc)
      past.sort(byStartAsc)

      return {
        ...editionGroup,
        lessons: [...future, ...past],
      }
    })

    editions.sort((a, b) => {
      const statusA = statusOrder[a.edition.status] || Number.MAX_SAFE_INTEGER
      const statusB = statusOrder[b.edition.status] || Number.MAX_SAFE_INTEGER
      if (statusA !== statusB) {
        return statusA - statusB
      }
      return a.edition.name.localeCompare(b.edition.name, undefined, { sensitivity: "base" })
    })

    g.editions = editions
    delete g.editionsMap
  }

  return Object.values(grouped).sort((a, b) =>
    a.course.name.localeCompare(b.course.name, undefined, { sensitivity: "base" })
  )
}

const getRecordingMeta = (lesson) => {
  if (lesson?.video_url) {
    return { label: "DISPONIBILE", color: "var(--sf-green)" }
  }

  return { label: "NON DISPONIBILE", color: "var(--sf-gray)" }
}

const formatDurationHhMmSs = (seconds) => {
  const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
}

const getLessonNavigationPath = (lesson) => {
  if (lesson?.room_name) {
    return `/lessons/${lesson.room_name}`
  }

  if (lesson?.video_url && lesson?.id) {
    return `/lessons/${lesson.id}`
  }

  return null
}

const Lessons = () => {
  const { t } = useTranslation()

  const [lessonsLoading, setLessonsLoading] = useState(false)
  const [lessons, setLessons] = useState([])
  const [filteredLessons, setFilteredLessons] = useState([])
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [tags, setTags] = useState([])
  const [courseCollapsableState, setCourseCollapsableState] = useState([])
  const [selectedEditionByCourse, setSelectedEditionByCourse] = useState({})
  const [searchQuery, setSearchQuery] = useState("")
  const [showOnlyToday, setShowOnlyToday] = useState(false)

  const syncCollapsedStateFromGroups = useCallback((groups) => {
    const courseIds = groups.map((group) => group.course.id)

    setCourseCollapsableState((prev) => mergeCollapsedState(prev, courseIds, true))
    setSelectedEditionByCourse((prev) => {
      const next = {}

      for (const group of groups) {
        const courseId = group.course.id
        const validEditionIds = group.editions.map((editionGroup) => editionGroup.edition.id)
        const previousSelection = prev[courseId]
        next[courseId] = validEditionIds.includes(previousSelection) ? previousSelection : null
      }

      return next
    })
  }, [])

  const applyFilters = useCallback((nextSearchQuery = "", nextShowOnlyToday = false, sourceLessons = lessons) => {
    const normalizedSearch = (nextSearchQuery || "").toLowerCase().trim()
    const now = new Date()

    const isToday = (dateValue) => {
      if (!dateValue) return false
      const date = new Date(dateValue)
      return (
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate()
      )
    }

    const filtered = sourceLessons.filter((lesson) => {
      const matchesSearch = !normalizedSearch || (
        lesson.name?.toLowerCase().trim().includes(normalizedSearch) ||
        lesson.module?.name?.toLowerCase().trim().includes(normalizedSearch) ||
        lesson.edition?.name?.toLowerCase().trim().includes(normalizedSearch) ||
        lesson.edition?.course?.name?.toLowerCase().trim().includes(normalizedSearch) ||
        lesson.description?.toLowerCase().trim().includes(normalizedSearch) ||
        (lesson.starts_at && lesson.starts_at.toLowerCase().trim().includes(normalizedSearch))
      )

      const matchesToday = !nextShowOnlyToday || isToday(lesson.starts_at)

      return matchesSearch && matchesToday
    })

    const groupedLessons = groupAndSortLessonsByCourse(filtered)
    setFilteredLessons(groupedLessons)
    syncCollapsedStateFromGroups(groupedLessons)
  }, [lessons, syncCollapsedStateFromGroups])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [selectedLesson])

  useEffect(() => {
    const getLessons = async () => {
      setLessonsLoading(true)
      try {
        const lessonsResponse = await api.get("/teacher/lessons", {
          params: { include_terminated: true },
        })
        setLessons(lessonsResponse)
      } catch (e) {
        console.error(e)
      }
      setLessonsLoading(false)
    }

    const getTags = async () => {
      try {
        const res = await api.get("/teacher/tags")
        setTags(res)
      } catch (e) {
        console.error(e)
      }
    }

    getLessons()
    getTags()
  }, [])

  useEffect(() => {
    applyFilters(searchQuery, showOnlyToday, lessons)
  }, [applyFilters, lessons, searchQuery, showOnlyToday])

  const onSearch = (searchText) => {
    const nextSearch = typeof searchText === "string" ? searchText : (searchText?.target?.value || "")
    setSearchQuery(nextSearch)
  }

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>Lezioni</title>
        </Helmet>
      </HelmetProvider>

      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            {
              selectedLesson === null && lessonsLoading === false &&
              <>
                <div className={styles.topRow}>
                  <div className={typo.title}>{t("lessons.title")}</div>
                  <Button
                    appearance="subtle"
                    onClick={() => {
                      setShowOnlyToday((prev) => !prev)
                    }}
                  >
                    {showOnlyToday ? "MOSTRA TUTTE" : "MOSTRA SOLO OGGI"}
                  </Button>
                </div>

                <TextInput
                  onKeyUp={onSearch}
                  type="search"
                  style={{ width: "100%" }}
                  placeholder={t("search")}
                />

                <div className={styles.courses}>
                  <Each
                    of={filteredLessons}
                    render={(courseGroup) => {
                      const isCourseCollapsed = courseCollapsableState.find((m) => m.id === courseGroup.course.id)?.collapsed
                      const selectedEditionId = selectedEditionByCourse[courseGroup.course.id] ?? null
                      const selectedEditionGroup = selectedEditionId
                        ? courseGroup.editions.find((editionGroup) => editionGroup.edition.id === selectedEditionId)
                        : null
                      const lessonsToShow = selectedEditionGroup
                        ? selectedEditionGroup.lessons
                        : courseGroup.editions.flatMap((editionGroup) => editionGroup.lessons)

                      return (
                        <Card
                          title={
                            <div
                              className={styles.courseHeader}
                              onClick={() => {
                                setCourseCollapsableState((prev) => prev.map((cs) =>
                                  cs.id === courseGroup.course.id ? { ...cs, collapsed: !cs.collapsed } : cs
                                ))
                              }}
                            >
                              {
                                courseGroup.course.thumbnail
                                  ? <img src={courseGroup.course.thumbnail} alt="" className={styles.courseThumbnail} />
                                  : <div className={styles.courseThumbnailFallback}>{courseGroup.course.name?.charAt(0) ?? "-"}</div>
                              }
                              <div className={styles.courseName}>{courseGroup.course.name}</div>
                            </div>
                          }
                        >
                          <ChevronIcon
                            className={`${styles.courseChevron} ${isCourseCollapsed ? styles.collapsed : ""}`}
                            onClick={() => {
                              setCourseCollapsableState((prev) => prev.map((cs) =>
                                cs.id === courseGroup.course.id ? { ...cs, collapsed: !cs.collapsed } : cs
                              ))
                            }}
                          />

                          <div className={`${styles.courseBody} ${isCourseCollapsed ? styles.collapsed : ""}`}>
                            <div className={styles.editionFilters}>
                              <button
                                className={`${styles.editionFilter} ${selectedEditionId === null ? styles.selected : ""}`}
                                onClick={() => {
                                  setSelectedEditionByCourse((prev) => ({ ...prev, [courseGroup.course.id]: null }))
                                }}
                              >
                                Tutto
                              </button>
                              <Each
                                of={courseGroup.editions}
                                render={(editionGroup) => (
                                  <button
                                    className={`${styles.editionFilter} ${selectedEditionId === editionGroup.edition.id ? styles.selected : ""}`}
                                    onClick={() => {
                                      setSelectedEditionByCourse((prev) => ({ ...prev, [courseGroup.course.id]: editionGroup.edition.id }))
                                    }}
                                  >
                                    {editionGroup.edition.name}
                                  </button>
                                )}
                              />
                            </div>

                            <Table
                              appearance="minimal"
                              onRowClick={(lesson) => {
                                const lessonPath = getLessonNavigationPath(lesson)
                                if (lessonPath) {
                                  window.open(lessonPath, "_blank")
                                }
                              }}
                              columns={[
                                { key: "name", label: "Lezione", type: "custom" },
                                { key: "module_name", label: "Modulo", type: "custom" },
                                { key: "edition_name", label: "Edizione", type: "custom" },
                                { key: "starts_at", label: "Inizio", type: "datetime" },
                                { key: "ends_at", label: "Fine", type: "datetime" },
                                { key: "duration", label: "Durata", type: "custom" },
                                { key: "recording", label: "Registrazione", type: "custom" },
                              ]}
                              elements={lessonsToShow.map((lesson) => {
                                const recording = getRecordingMeta(lesson)
                                const isRecordedLesson = lesson?.type === "recorded"

                                return {
                                  ...lesson,
                                  starts_at: isRecordedLesson ? null : lesson.starts_at,
                                  ends_at: isRecordedLesson ? null : lesson.ends_at,
                                  name: {
                                    value: lesson.name ?? "-",
                                    type: "string",
                                    component: <div className={styles.clampedLabel}>{lesson.name ?? "-"}</div>,
                                  },
                                  module_name: {
                                    value: lesson.module?.name ?? "-",
                                    type: "string",
                                    component: <div className={styles.clampedLabel}>{lesson.module?.name ?? "-"}</div>,
                                  },
                                  edition_name: {
                                    value: lesson.edition?.name ?? "-",
                                    type: "string",
                                    component: <div className={styles.clampedLabel}>{lesson.edition?.name ?? "-"}</div>,
                                  },
                                  duration: {
                                    value: isRecordedLesson ? Number(lesson.video_duration ?? 0) : -1,
                                    type: "string",
                                    component: <div>{isRecordedLesson ? formatDurationHhMmSs(lesson.video_duration) : "-"}</div>,
                                  },
                                  recording: {
                                    value: recording.label,
                                    type: "string",
                                    component: <Badge size="small" name={recording.label} color={recording.color} />,
                                  },
                                }
                              })}
                            />
                          </div>
                        </Card>
                      )
                    }}
                  />
                </div>
              </>
            }

            {
              selectedLesson === null && lessonsLoading === false && filteredLessons.length === 0 &&
              <EmptyContent
                Illustration={EmptyIllustration}
                title={"Non ci sono ancora lezioni"}
                message={t("courses.noLessonsAdvice")}
              />
            }

            {
              selectedLesson === null && lessonsLoading === true &&
              <>
                <div className={typo.title}>{t("lessons.title")}</div>
                <Each
                  of={[0, 1]}
                  render={() => (
                    <Card>
                      <div className={styles.courseSkeleton}>
                        <div className={styles.courseSkeletonHeader}>
                          <Skeleton type="rect" width="256px" height={"22px"} borderRadius={"8px"} />
                        </div>
                      </div>
                    </Card>
                  )}
                />
              </>
            }

            {
              selectedLesson &&
              <>
                <Back onClick={() => setSelectedLesson(null)} />
                <LessonEdit lesson={selectedLesson} tagsList={tags} onSave={() => { }} />
              </>
            }
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  )
}

export default Lessons
